import React, { Fragment, useContext, useState } from 'react';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { Save, X, Calendar, Award, Gift, Clock } from 'react-feather';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchAddMiniGameRVOPV } from '../../../../api/minigame';
import { toast } from 'react-toastify';

const NewMiniGameRVOPV = () => {
  const history = useNavigate();
  const { layoutURL } = useContext(CustomizerContext);
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      reward: '',
      status: 'active'
    }
  });

  const startDate = watch('startDate');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Kiểm tra ngày kết thúc phải sau ngày bắt đầu
      if (new Date(data.endDate) <= new Date(data.startDate)) {
        toast.error('Ngày kết thúc phải sau ngày bắt đầu!');
        setLoading(false);
        return;
      }

      // Chuẩn bị data để gửi API
      const minigameData = {
        title: data.title,
        description: data.description,
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
        reward: Number(data.reward),
        status: data.status
      };

      const response = await fetchAddMiniGameRVOPV(minigameData);
      
      toast.success('🎮 Tạo minigame RVOPV thành công!');
      reset();
      
      // Redirect về trang danh sách sau 1.5s
      setTimeout(() => {
        history(`${process.env.PUBLIC_URL}/dashboard/UserRVOPV/${layoutURL}`);
      }, 1500);
    } catch (error) {
      const errorMsg = error.message || 'Không thể tạo minigame';
      toast.error('❌ Lỗi: ' + errorMsg);
      console.error('Error creating minigame:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Bạn có chắc chắn muốn hủy? Dữ liệu đã nhập sẽ bị mất.')) {
      reset();
      history(`${process.env.PUBLIC_URL}/dashboard/UserRVOPV/${layoutURL}`);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent='MiniGame RVOPV' title='Tạo MiniGame' mainTitle='Tạo MiniGame RVOPV Mới' />
      <Container fluid={true}>
        <Row className="justify-content-center">
          <Col lg="10" xl="9">
            <Card className="shadow-sm border-0">
              <CardBody className="p-4 p-md-5">
                {/* Header */}
                <div className="mb-4 pb-3 border-bottom">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-gradient rounded-circle p-3 me-3">
                      <Gift size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="mb-1">Tạo MiniGame RVOPV Mới</h3>
                      <p className="text-muted mb-0">Điền đầy đủ thông tin để tạo minigame</p>
                    </div>
                  </div>
                </div>

                <Form onSubmit={handleSubmit(onSubmit)}>
                  {/* Tên MiniGame */}
                  <FormGroup className="mb-4">
                    <Label className="form-label fw-bold fs-6">
                      <Gift size={16} className="me-2 text-primary" />
                      Tên MiniGame <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Ví dụ: Vòng quay may mắn RVOPV 2024"
                      {...register('title', { 
                        required: 'Tên minigame là bắt buộc',
                        minLength: { value: 3, message: 'Tên phải có ít nhất 3 ký tự' },
                        maxLength: { value: 100, message: 'Tên không được quá 100 ký tự' }
                      })}
                      invalid={!!errors.title}
                      className="form-control-lg"
                    />
                    {errors.title && (
                      <div className="invalid-feedback d-block">
                        {errors.title.message}
                      </div>
                    )}
                  </FormGroup>

                  {/* Mô tả */}
                  <FormGroup className="mb-4">
                    <Label className="form-label fw-bold fs-6">
                      Mô tả chi tiết <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      rows="5"
                      placeholder="Nhập mô tả về minigame: mục đích, cách chơi, luật lệ, giải thưởng..."
                      {...register('description', { 
                        required: 'Mô tả là bắt buộc',
                        minLength: { value: 10, message: 'Mô tả phải có ít nhất 10 ký tự' },
                        maxLength: { value: 1000, message: 'Mô tả không được quá 1000 ký tự' }
                      })}
                      invalid={!!errors.description}
                      style={{ fontSize: '15px' }}
                    />
                    {errors.description && (
                      <div className="invalid-feedback d-block">
                        {errors.description.message}
                      </div>
                    )}
                  </FormGroup>

                  {/* Thời gian */}
                  <div className="bg-light p-3 rounded mb-4">
                    <h6 className="mb-3">
                      <Clock size={18} className="me-2 text-info" />
                      Thời gian diễn ra
                    </h6>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label className="form-label fw-semibold">
                            <Calendar size={14} className="me-2" />
                            Ngày giờ bắt đầu <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="datetime-local"
                            {...register('startDate', { 
                              required: 'Ngày bắt đầu là bắt buộc'
                            })}
                            invalid={!!errors.startDate}
                            className="form-control-lg"
                          />
                          {errors.startDate && (
                            <div className="invalid-feedback d-block">
                              {errors.startDate.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label className="form-label fw-semibold">
                            <Calendar size={14} className="me-2" />
                            Ngày giờ kết thúc <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="datetime-local"
                            {...register('endDate', { 
                              required: 'Ngày kết thúc là bắt buộc'
                            })}
                            invalid={!!errors.endDate}
                            min={startDate}
                            className="form-control-lg"
                          />
                          {errors.endDate && (
                            <div className="invalid-feedback d-block">
                              {errors.endDate.message}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  {/* Phần thưởng và Trạng thái */}
                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-4">
                        <Label className="form-label fw-bold fs-6">
                          <Award size={16} className="me-2 text-warning" />
                          Phần thưởng (điểm) <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="number"
                          placeholder="Nhập số điểm thưởng..."
                          min="0"
                          step="1"
                          {...register('reward', { 
                            required: 'Phần thưởng là bắt buộc',
                            min: { value: 0, message: 'Phần thưởng phải >= 0' },
                            validate: value => Number.isInteger(Number(value)) || 'Phần thưởng phải là số nguyên'
                          })}
                          invalid={!!errors.reward}
                          className="form-control-lg"
                        />
                        {errors.reward && (
                          <div className="invalid-feedback d-block">
                            {errors.reward.message}
                          </div>
                        )}
                        <small className="text-muted d-block mt-2">
                          💡 Số điểm người chơi nhận được khi hoàn thành
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-4">
                        <Label className="form-label fw-bold fs-6">
                          Trạng thái ban đầu <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          {...register('status')}
                          className="form-control-lg"
                        >
                          <option value="active">✅ Đang hoạt động</option>
                          <option value="inactive">⏸️ Tạm dừng</option>
                          <option value="completed">✔️ Đã kết thúc</option>
                        </Input>
                        <small className="text-muted d-block mt-2">
                          💡 Có thể thay đổi trạng thái sau khi tạo
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-end gap-3 mt-5 pt-4 border-top">
                    <Button
                      color="light"
                      type="button"
                      onClick={handleCancel}
                      disabled={loading}
                      className="px-4 py-2 fw-semibold"
                      size="lg"
                    >
                      <X size={18} className="me-2" />
                      Hủy bỏ
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={loading}
                      className="px-5 py-2 fw-semibold shadow-sm"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Đang tạo...
                        </>
                      ) : (
                        <>
                          <Save size={18} className="me-2" />
                          Tạo MiniGame
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>

            {/* Info Card */}
            <Card className="mt-3 border-0 bg-light">
              <CardBody className="p-3">
                <small className="text-muted d-block">
                  <strong>📌 Lưu ý:</strong>
                  <ul className="mb-0 mt-2 ps-3">
                    <li>Ngày kết thúc phải sau ngày bắt đầu</li>
                    <li>Phần thưởng phải là số nguyên dương</li>
                    <li>Có thể chỉnh sửa thông tin sau khi tạo</li>
                    <li>Trạng thái có thể thay đổi bất cứ lúc nào</li>
                  </ul>
                </small>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default NewMiniGameRVOPV;
